import { useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { Area, Bar, CartesianGrid, Cell, ComposedChart, Line, XAxis, YAxis } from 'recharts'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ChartContainer, ChartTooltip, type ChartConfig } from '@/components/ui/chart'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import type { AdminChartRow } from '@/services/dashboard'
import {
    formatAverage,
    formatAxis,
    formatValue,
    growthSeries,
    metricTotal,
    mrrSeries,
    sparklineValues,
    type Granularity,
    type GrowthMetric,
    type GrowthPoint,
} from '@/lib/growth-chart'

const CHART_COLOR = '#6366f1'

const chartConfig = {
    value: { label: 'Value', color: CHART_COLOR },
} satisfies ChartConfig

interface MetricOption {
    key: GrowthMetric
    label: string
    money?: boolean
    description: (unit: Granularity) => string
}

// Each tab counts what the card above it counts, over the same whole UTC days.
const METRICS: MetricOption[] = [
    { key: 'signups', label: 'New signups', description: (unit) => `Agencies by the ${unit} they signed up` },
    { key: 'mrr', label: 'MRR', money: true, description: () => "Same as the MRR card. Built from agencies paying today, so past churn isn't shown" },
    { key: 'submissions', label: 'Completed submissions', description: (unit) => `Submissions marked complete, by the ${unit} they came in` },
    { key: 'leads', label: 'Leads', description: () => 'New leads, complete and partial, as in the Total Leads card' },
    { key: 'appointments', label: 'Appointments', description: (unit) => `Appointments by the ${unit} they were booked` },
]

interface GrowthCardProps {
    /** GET /admin/dashboard/chart rows; null while they load. */
    rows: AdminChartRow[] | null
    /** The MRR card's value, which the MRR tab ends on; null while stats load. */
    currentMrr: number | null
    /** Shown instead of the card's content when the request failed. */
    error?: ReactNode
}

/**
 * The dashboard's trend chart: one tab per metric with its total and a small
 * trend line, then that metric by day (with a 7-day average) or by week, as a
 * chart or a table. Today's bar is drawn as incomplete so a half day doesn't
 * read as a drop. MRR is a level, not a count, so it is drawn as a line.
 */
export function GrowthCard({ rows, currentMrr, error }: GrowthCardProps) {
    const [metric, setMetric] = useState<GrowthMetric>('signups')
    const [granularity, setGranularity] = useState<Granularity>('day')
    const [view, setView] = useState<'chart' | 'table'>('chart')
    const tabRefs = useRef<(HTMLButtonElement | null)[]>([])

    const option = METRICS.find((m) => m.key === metric) ?? METRICS[0]
    const isMrr = metric === 'mrr'
    const points = useMemo(() => {
        if (!rows) return null
        if (metric === 'mrr') return currentMrr === null ? null : mrrSeries(rows, currentMrr, granularity)
        return growthSeries(rows, metric, granularity)
    }, [rows, currentMrr, metric, granularity])

    // Arrow keys, Home and End move between tabs, as the tabs pattern expects.
    const onTabKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        const current = METRICS.findIndex((m) => m.key === metric)
        const last = METRICS.length - 1
        const moves: Partial<Record<string, number>> = {
            ArrowRight: current === last ? 0 : current + 1,
            ArrowLeft: current === 0 ? last : current - 1,
            Home: 0,
            End: last,
        }
        const next = moves[event.key]
        if (next === undefined) return
        event.preventDefault()
        setMetric(METRICS[next].key)
        tabRefs.current[next]?.focus()
    }

    if (error) {
        return (
            <Card className="py-0 gap-0">
                <CardContent className="p-6">{error}</CardContent>
            </Card>
        )
    }

    // A tab's total and trend line, or null until its data has loaded.
    const tabSummary = (key: GrowthMetric): { total: number; trend: number[] } | null => {
        if (!rows) return null
        if (key !== 'mrr') return { total: metricTotal(rows, key), trend: sparklineValues(rows, key) }
        if (currentMrr === null) return null
        return { total: currentMrr, trend: mrrSeries(rows, currentMrr, 'day').map((point) => point.value) }
    }
    const showAverage = !isMrr && granularity === 'day'

    return (
        <Card className="py-0 gap-0 overflow-hidden">
            <div role="tablist" aria-label="Metric" onKeyDown={onTabKeyDown} className="flex overflow-x-auto divide-x border-b">
                {METRICS.map((m, i) => {
                    const selected = m.key === metric
                    const summary = tabSummary(m.key)
                    return (
                        <button
                            key={m.key}
                            ref={(el) => { tabRefs.current[i] = el }}
                            type="button"
                            role="tab"
                            id={`growth-tab-${m.key}`}
                            aria-selected={selected}
                            aria-controls="growth-panel"
                            tabIndex={selected ? 0 : -1}
                            onClick={() => setMetric(m.key)}
                            className={cn(
                                'relative flex min-w-[150px] flex-1 flex-col gap-2 p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                                selected ? 'bg-card' : 'bg-muted/40 hover:bg-muted/70'
                            )}
                        >
                            {selected && <span className="absolute inset-x-0 top-0 h-0.5" style={{ backgroundColor: CHART_COLOR }} />}
                            <span className={cn('text-sm font-medium', selected ? 'text-foreground' : 'text-muted-foreground')}>
                                {m.label}
                            </span>
                            {summary ? (
                                <>
                                    <span className="text-2xl font-semibold leading-tight text-foreground">
                                        {formatValue(summary.total, m.money)}
                                    </span>
                                    <Sparkline values={summary.trend} active={selected} />
                                </>
                            ) : (
                                <>
                                    <Skeleton className="h-7 w-[70px]" />
                                    <Skeleton className="h-6 w-full" />
                                </>
                            )}
                        </button>
                    )
                })}
            </div>

            <CardContent id="growth-panel" role="tabpanel" aria-labelledby={`growth-tab-${metric}`} className="flex flex-col gap-4 p-6">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex flex-col gap-0.5">
                        <h2 className="text-lg font-semibold">
                            {option.label} {isMrr ? 'by' : 'per'} {granularity}
                        </h2>
                        <p className="text-sm text-muted-foreground">
                            {option.description(granularity)} · UTC days
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                        <Legend granularity={granularity} isMrr={isMrr} />
                        <Segmented
                            label="Group by"
                            value={granularity}
                            onChange={setGranularity}
                            options={[{ value: 'day', label: 'Day' }, { value: 'week', label: 'Week' }]}
                        />
                        <Segmented
                            label="Show as"
                            value={view}
                            onChange={setView}
                            options={[{ value: 'chart', label: 'Chart' }, { value: 'table', label: 'Table' }]}
                        />
                    </div>
                </div>

                {!points ? (
                    <Skeleton className="h-[320px] w-full rounded-lg" />
                ) : points.length === 0 ? (
                    <div className="h-[320px] flex items-center justify-center text-muted-foreground">
                        No data available for this period
                    </div>
                ) : view === 'chart' ? (
                    <ChartContainer config={chartConfig} className="h-[320px] w-full">
                        <ComposedChart data={points} margin={{ left: 0, right: 12, top: 12, bottom: 0 }}>
                            <CartesianGrid vertical={false} />
                            <XAxis dataKey="label" axisLine={false} tickLine={false} tickMargin={8} minTickGap={24} />
                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                tickMargin={8}
                                width={56}
                                allowDecimals={false}
                                domain={isMrr ? ['auto', 'auto'] : [0, 'auto']}
                                tickFormatter={(value: number) => formatAxis(value, option.money)}
                            />
                            <ChartTooltip content={<GrowthTooltip metricLabel={option.label} money={option.money} />} />
                            {isMrr && (
                                <Area
                                    dataKey="value"
                                    type="linear"
                                    stroke="var(--color-value)"
                                    strokeWidth={2}
                                    fill="var(--color-value)"
                                    fillOpacity={0.1}
                                    dot={false}
                                    isAnimationActive={false}
                                />
                            )}
                            {!isMrr && (
                                <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={24} isAnimationActive={false}>
                                    {points.map((point) => (
                                        <Cell
                                            key={point.key}
                                            fill="var(--color-value)"
                                            fillOpacity={point.incomplete ? 0.2 : 1}
                                            stroke={point.incomplete ? 'var(--color-value)' : 'none'}
                                            strokeWidth={point.incomplete ? 1.5 : 0}
                                            strokeDasharray={point.incomplete ? '3 3' : undefined}
                                        />
                                    ))}
                                </Bar>
                            )}
                            {showAverage && (
                                <Line
                                    dataKey="average"
                                    type="linear"
                                    stroke="var(--foreground)"
                                    strokeWidth={2}
                                    dot={false}
                                    activeDot={false}
                                    isAnimationActive={false}
                                />
                            )}
                        </ComposedChart>
                    </ChartContainer>
                ) : (
                    <div className="max-h-[320px] overflow-auto rounded-lg border">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{granularity === 'day' ? 'Day' : 'Week'}</TableHead>
                                    <TableHead className="text-right">{option.label}</TableHead>
                                    {showAverage && <TableHead className="text-right">7-day average</TableHead>}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {[...points].reverse().map((point) => (
                                    <TableRow key={point.key}>
                                        <TableCell>
                                            {point.title}
                                            {point.note && <span className="text-muted-foreground"> · {point.note}</span>}
                                        </TableCell>
                                        <TableCell className="text-right font-medium tabular-nums">
                                            {formatValue(point.value, option.money)}
                                        </TableCell>
                                        {showAverage && (
                                            <TableCell className="text-right tabular-nums text-muted-foreground">
                                                {point.average === null ? '' : formatAverage(point.average, option.money)}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </CardContent>
        </Card>
    )
}

function Sparkline({ values, active }: { values: number[]; active: boolean }) {
    if (values.length < 2) return <div className="h-6" />
    const min = Math.min(...values)
    const span = Math.max(...values) - min || 1
    const path = values
        .map((value, i) => `${i ? 'L' : 'M'}${((i / (values.length - 1)) * 100).toFixed(2)} ${(22 - ((value - min) / span) * 20).toFixed(2)}`)
        .join(' ')
    return (
        <svg viewBox="0 0 100 24" preserveAspectRatio="none" className="h-6 w-full" aria-hidden="true">
            <path
                d={path}
                fill="none"
                strokeWidth={1.5}
                strokeLinejoin="round"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
                style={{ stroke: active ? CHART_COLOR : 'var(--muted-foreground)', opacity: active ? 1 : 0.5 }}
            />
        </svg>
    )
}

function Legend({ granularity, isMrr }: { granularity: Granularity; isMrr: boolean }) {
    if (isMrr) {
        return (
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5">
                    <span className="h-0.5 w-3.5 rounded-full" style={{ backgroundColor: CHART_COLOR }} />
                    MRR at the end of each {granularity}
                </span>
            </div>
        )
    }
    return (
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-[3px]" style={{ backgroundColor: CHART_COLOR }} />
                Per {granularity}
            </span>
            {granularity === 'day' && (
                <span className="flex items-center gap-1.5">
                    <span className="h-0.5 w-3.5 rounded-full bg-foreground" />
                    7-day average
                </span>
            )}
            <span className="flex items-center gap-1.5">
                <span
                    className="h-2.5 w-2.5 rounded-[3px] border border-dashed"
                    style={{ borderColor: CHART_COLOR, backgroundColor: 'rgba(99, 102, 241, 0.2)' }}
                />
                Incomplete
            </span>
        </div>
    )
}

function Segmented<T extends string>({
    label,
    value,
    onChange,
    options,
}: {
    label: string
    value: T
    onChange: (value: T) => void
    options: { value: T; label: string }[]
}) {
    return (
        <div role="group" aria-label={label} className="flex items-center bg-muted/40 p-0.5 rounded-full border border-border/50">
            {options.map((opt) => (
                <button
                    key={opt.value}
                    type="button"
                    aria-pressed={value === opt.value}
                    onClick={() => onChange(opt.value)}
                    className={cn(
                        'px-3 py-1 rounded-full text-xs font-medium transition-colors border',
                        value === opt.value
                            ? 'bg-background shadow-sm text-foreground border-border/50'
                            : 'text-muted-foreground hover:text-foreground border-transparent'
                    )}
                >
                    {opt.label}
                </button>
            ))}
        </div>
    )
}

interface GrowthTooltipProps {
    active?: boolean
    payload?: { payload: GrowthPoint }[]
    metricLabel: string
    money?: boolean
}

function GrowthTooltip({ active, payload, metricLabel, money }: GrowthTooltipProps) {
    const point = active ? payload?.[0]?.payload : undefined
    if (!point) return null
    return (
        <div className="grid min-w-[190px] gap-1.5 rounded-lg border border-border/50 bg-background px-3 py-2 text-xs shadow-xl">
            <div className="font-medium text-muted-foreground">{point.title}</div>
            <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 shrink-0 rounded-[2px]" style={{ backgroundColor: CHART_COLOR }} />
                <span className="flex-1 text-muted-foreground">{metricLabel}</span>
                <span className="font-mono font-medium tabular-nums text-foreground">{formatValue(point.value, money)}</span>
            </div>
            {point.average !== null && (
                <div className="flex items-center gap-2">
                    <span className="h-0.5 w-2.5 shrink-0 rounded-full bg-foreground" />
                    <span className="flex-1 text-muted-foreground">7-day average</span>
                    <span className="font-mono font-medium tabular-nums text-foreground">{formatAverage(point.average, money)}</span>
                </div>
            )}
            {point.note && <div className="border-t border-border/50 pt-1.5 text-muted-foreground">{point.note}</div>}
        </div>
    )
}
